import { Loader2 } from 'lucide-react';
import { twMerge as cn } from 'tailwind-merge';

export type LoadingSpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SPINNER_BOX: Record<LoadingSpinnerSize, string> = {
  xs: 'size-3.5',
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-8',
};

export interface LoadingSpinnerProps {
  size?: LoadingSpinnerSize;
  className?: string;
}

/** Lucide spinners sit off-center in flex rows; fixed box + centered SVG fixes alignment. */
export function LoadingSpinner({
  size = 'md',
  className,
}: LoadingSpinnerProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center leading-none',
        SPINNER_BOX[size],
      )}
      aria-hidden
    >
      <Loader2
        className={cn('size-full animate-spin', className)}
        strokeWidth={2.25}
      />
    </span>
  );
}

export type LoadingIndicatorLayout = 'inline' | 'page';

export interface LoadingIndicatorProps {
  label?: string;
  size?: LoadingSpinnerSize;
  layout?: LoadingIndicatorLayout;
  className?: string;
  spinnerClassName?: string;
}

export function LoadingIndicator({
  label = 'Loading...',
  size = 'md',
  layout = 'inline',
  className,
  spinnerClassName,
}: LoadingIndicatorProps) {
  if (layout === 'page') {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'flex w-full flex-1 items-center justify-center',
          className,
        )}
      >
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <LoadingSpinner
            size="xl"
            className={cn('text-brand', spinnerClassName)}
          />
          {label ? (
            <span className="text-sm font-medium leading-normal">{label}</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-2.5 text-gray-600',
        className,
      )}
    >
      <LoadingSpinner size={size} className={spinnerClassName} />
      {label ? (
        <span className="text-sm font-medium leading-normal">{label}</span>
      ) : null}
    </div>
  );
}
